import { Box, Divider } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { editProjectLanguages, Project, qk } from 'api'
import { ILanguages } from 'api/languages'
import { DialogContent, Loader } from 'elements/feedback'
import { ToastContent } from 'elements/feedback/toast'
import { MultipleAutocomplete } from 'elements/inputs/autocomplete'
import { getInputError, useValidations } from 'lib/form-utils'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const formId = 'ADD_LANGUAGES_FORM'

type FormValues = {
  languages: Array<string>
}

type AddLanguagesFormProps = {
  project: Project
  closeDialog: () => void
  languages?: ILanguages
  isLoading: boolean
}

export const AddLanguagesForm = ({
  project,
  closeDialog,
  isLoading,
  languages,
}: AddLanguagesFormProps) => {
  const { t } = useTranslation()

  const { projectId, languages: addedLanguages } = project

  const rules = useValidations()

  const queryClient = useQueryClient()

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      languages: [],
    },
  })

  const $editProjectLanguages = useMutation(editProjectLanguages)

  if (isLoading) return <Loader />

  if (!languages) return null

  const availableLanguages = languages.filter(
    language => !addedLanguages.includes(language.code),
  )

  return (
    <DialogContent
      title={t('projects.settings.add_new_languages')}
      isActionLoading={$editProjectLanguages.isLoading}
      onDeny={closeDialog}
      externalFormId={formId}
      confirmText={t('common.add')}
    >
      <Box
        id={formId}
        component="form"
        onSubmit={handleSubmit(values =>
          $editProjectLanguages.mutate(
            {
              projectId,
              languages: [...addedLanguages, ...values.languages],
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(
                  qk.projects.details.toKeyWithArgs({ projectId }),
                )
                closeDialog()
                toast.success(
                  <ToastContent
                    title={t(
                      'projects.settings.languages_list_successfully_updated',
                    )}
                  />,
                )
              },
              onError: closeDialog,
            },
          ),
        )}
      >
        <Controller
          control={control}
          name="languages"
          rules={rules.required}
          render={({ field, fieldState }) => (
            <MultipleAutocomplete
              label={t('projects.create.select_languages')}
              {...field}
              {...getInputError(fieldState.error)}
              options={availableLanguages.map(language => ({
                value: language.code,
                label: t(`languages.${language.code}`),
              }))}
            />
          )}
        />

        <Divider color="divider" sx={{ mt: 5, borderBottomWidth: 1 }} />
      </Box>
    </DialogContent>
  )
}
